import React, { useCallback, useId } from "react";
import styled, { useTheme } from "styled-components/native";
import { Dimensions, Modal, Platform, TouchableOpacity } from "react-native";
import { ModalProps } from "@components/atoms/SlideSheet/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBText } from "..";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SpaceType } from "../Spacer/types";
import { VDBIcons } from "@utils/VDBIcons";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import Animated, {
  withTiming,
  useSharedValue,
  useAnimatedStyle,
} from "react-native-reanimated";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { css } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { DefaultTheme } from "styled-components/native";
import { useEscapeKey } from "@hooks/accessibility";

const SlideSheet = ({
  headerTitle,
  children,
  onClose,
  isOpen,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  minModalHeight,
  backgroundColor,
  skipKeyboardAware,
  fillContentHeight,
  fitContent = false,
  allowFullScreen,
  height = "auto",
  clearChat = false,
  handleClearChat = () => {},
  hideClearAllButton = true,
  handleClearAll = () => {},
  leftIcon,
  leftIconPress,
}: ModalProps) => {
  const typography = useTypographyStore();
  const handleCloseModal = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const theme = useTheme();
  const userInfo = useUserInfo();
  const windowHeight = Dimensions.get(
    allowFullScreen ? "screen" : "window"
  ).height;
  const { deviceWidth: width, isFold } = useIsFoldable();
  const opacity = useSharedValue(isOpen ? 1 : 0);
  const translateY = useSharedValue(isOpen ? 0 : windowHeight);

  // Generate unique ID for ARIA labeling
  const modalId = useId();
  const titleId = `${modalId}-title`;

  // Handle Escape key for web (tablet with keyboard)
  useEscapeKey({
    isActive: isOpen,
    onEscape: handleCloseModal,
  });

  React.useEffect(() => {
    opacity.value = withTiming(isOpen ? 1 : 0, { duration: 300 });
    translateY.value = withTiming(isOpen ? 0 : windowHeight, { duration: 300 });
  }, [isOpen]);

  const backgroundStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const overlayStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={isOpen}
      supportedOrientations={["portrait", "landscape"]}
    >
      <BackgroundAnimated
        style={backgroundStyle}
        iFrame={userInfo.isIFrame()}
      />
      <OverlayAnimated style={overlayStyle}>
        <ModalBox
          backgroundColor={backgroundColor}
          height={height}
          isContentHeight={fitContent}
          minModalHeight={minModalHeight}
          windowHeight={windowHeight}
          windowWidth={width}
          isFold={isFold}
          iFrame={userInfo.isIFrame()}
          accessibilityViewIsModal={true}
          accessible={true}
          accessibilityRole="none"
        >
          <OverlapContainer>
            {!hideClearAllButton && (
              <ClearAllButton>
                <TouchableOpacity onPress={handleClearAll}>
                  <VDBText
                    type={typography["Body-M"]}
                    color={theme["link-static"]}
                    textAlign="center"
                  >
                    Clear All
                  </VDBText>
                </TouchableOpacity>
              </ClearAllButton>
            )}
            <Header>
              <VDBText
                textAlign="center"
                type={typography["H2-M"]}
                color={theme["core-05"]}
                nativeID={titleId}
                accessibilityRole="header"
              >
                {headerTitle}
              </VDBText>
            </Header>

            <ButtonContainer clearChat={clearChat}>
              {leftIcon && (
                <TouchableOpacity onPress={leftIconPress}>
                  <VDBIcon
                    name={leftIcon}
                    variant={IconsVariant.VDBCustom}
                    size={24}
                    color={theme["core-06"]}
                  />
                </TouchableOpacity>
              )}
              {clearChat && (
                <TouchableOpacity
                  onPress={handleClearChat}
                  accessibilityRole="button"
                  accessibilityLabel="Clear chat"
                >
                  <VDBText
                    textAlign="center"
                    type={typography["H2-M"]}
                    color={theme["core-05"]}
                  >
                    Clear Chat
                  </VDBText>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                onPress={handleCloseModal}
                accessibilityRole="button"
                accessibilityLabel="Close modal"
              >
                <VDBIcon
                  name={VDBIcons.CLOSE_CIRCLE}
                  variant={IconsVariant.VDBCustom}
                  size={24}
                  color={theme["core-06"]}
                />
              </TouchableOpacity>
            </ButtonContainer>
          </OverlapContainer>

          <LineView />
          <ScrollViewContainer
            paddingLeft={paddingLeft}
            paddingRight={paddingRight}
            paddingBottom={paddingBottom}
            paddingTop={paddingTop}
            isContentHeight={fitContent}
            iFrame={userInfo.isIFrame()}
          >
            {!skipKeyboardAware ? (
              <KeyboardAwareScrollView
                bottomOffset={20}
                keyboardShouldPersistTaps="always"
                style={fitContent ? undefined : { flex: 1 }}
                contentContainerStyle={fitContent ? { flexGrow: 0 } : undefined}
              >
                {children}
              </KeyboardAwareScrollView>
            ) : (
              <FullModal
                fillContentHeight={fillContentHeight}
                isContentHeight={fitContent}
              >
                {children}
              </FullModal>
            )}
          </ScrollViewContainer>
        </ModalBox>
      </OverlayAnimated>
    </Modal>
  );
};

const FullModal = styled.View<{
  fillContentHeight?: boolean;
  isContentHeight?: boolean;
}>`
  ${({
    fillContentHeight,
    isContentHeight,
  }: {
    fillContentHeight?: boolean;
    isContentHeight?: boolean;
  }) => {
    if (isContentHeight) {
      return ``;
    }
    return fillContentHeight
      ? css`
          flex: 1;
        `
      : css`
          height: 100%;
        `;
  }}
  ${Platform.OS === "web" &&
  css`
    min-height: 0;
  `}
`;

const OverlayAnimated = styled(Animated.View)`
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
`;

const BackgroundAnimated = styled(Animated.View)<{ iFrame?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  ${({ iFrame }: { iFrame?: boolean }) =>
    iFrame &&
    Platform.OS === "web" &&
    css`
      overscroll-behavior: contain;
    `}
`;

const ModalBox = styled.View<{
  backgroundColor?: string;
  windowHeight?: any;
  height?: any;
  isContentHeight?: boolean;
  minModalHeight?: number;
  windowWidth?: any;
  isFold?: any;
  iFrame?: boolean;
}>`
  background-color: ${({
    theme,
    backgroundColor,
  }: {
    theme: DefaultTheme;
    backgroundColor?: string;
  }) => (backgroundColor ? backgroundColor : theme["core-01"])};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  height: ${({
    height,
    isContentHeight,
  }: {
    height?: string;
    isContentHeight?: boolean;
  }) => (isContentHeight ? "auto" : `${height}`)};
  min-height: ${({
    height,
    isContentHeight,
    minModalHeight,
  }: {
    height?: string;
    isContentHeight?: boolean;
    minModalHeight?: number;
  }) => {
    if (minModalHeight) {
      return `${minModalHeight}px`;
    }
    return isContentHeight ? "auto" : `${height}`;
  }};
  max-height: ${({ windowHeight }: { windowHeight?: number }) =>
    windowHeight ? `${windowHeight * 0.9}px` : "90%"};
  ${({ windowWidth, isFold }: { windowWidth?: number; isFold?: boolean }) =>
    isFold &&
    css`
      width: ${windowWidth}px;
      align-self: center;
    `}
  ${({ iFrame }: { iFrame?: boolean }) =>
    iFrame &&
    Platform.OS === "web" &&
    css`
      overscroll-behavior: contain;
    `}
  ${Platform.OS === "web" &&
  css`
    display: flex;
    flex-direction: column;
  `}
`;

const OverlapContainer = styled.View`
  position: relative;
  flex-direction: row;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  flex-shrink: 0;
`;

const Header = styled.View<any>`
  flex: 1;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  padding-top: ${({ theme }: { theme: DefaultTheme }) =>
    SpaceType.Double +
    (Platform.OS === "ios" && (theme.isDesktop || theme.isTablet) ? 2 : 0)}px;
  padding-bottom: ${({ theme }: { theme: DefaultTheme }) =>
    SpaceType.Double +
    (Platform.OS === "ios" && (theme.isDesktop || theme.isTablet) ? 2 : 0)}px;
`;

const ButtonContainer = styled.View<{ clearChat?: boolean }>`
  position: absolute;
  top: 1px;
  right: 2px;
  margin: ${SpaceType.Extra}px;
  ${({ clearChat }: { clearChat?: boolean }) =>
    clearChat &&
    css`
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: ${SpaceType.Basic}px;
    `}
`;

const ClearAllButton = styled.View`
  height: 100%;
  position: absolute;
  left: 16px;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LineView = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  flex-shrink: 0;
`;

const ScrollViewContainer = styled.View<{
  isContentHeight?: boolean;
  paddingLeft?: any;
  paddingRight?: any;
  paddingTop?: any;
  paddingBottom?: any;
  iFrame?: boolean;
}>`
  ${({ isContentHeight }: { isContentHeight?: boolean }) =>
    isContentHeight
      ? css`
          flex-grow: 0;
          flex-shrink: 1;
        `
      : css`
          flex: 1;
          flex-basis: auto;
          height: 100%;
        `}
  ${({ paddingLeft }: { paddingLeft?: number }) =>
    paddingLeft ? `padding-left: ${paddingLeft}px;` : `padding-left: 0px;`}
  ${({ paddingRight }: { paddingRight?: number }) =>
    paddingRight ? `padding-right: ${paddingRight}px;` : `padding-right: 0px;`}
  ${({ paddingTop }: { paddingTop?: number }) =>
    paddingTop ? `padding-top: ${paddingTop}px;` : `padding-top: 0px;`}
  ${({ paddingBottom }: { paddingBottom?: number }) =>
    paddingBottom
      ? `padding-bottom: ${paddingBottom}px;`
      : `padding-bottom: 0px;`}
  ${({ iFrame }: { iFrame?: boolean }) =>
    iFrame &&
    Platform.OS === "web" &&
    css`
      overscroll-behavior: contain;
    `}
  ${Platform.OS === "web" &&
  css`
    min-height: 0;
  `}
`;

export default SlideSheet;
